import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { adminQueries } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import { formatDate, hasPermission } from '@blimo/shared';
import { Badge, Button, DataTable, ErrorState, PageHeader } from '@blimo/ui';
import { FilterBar, FilterSelect } from '../../components/filter-bar.jsx';
import { PropertyLink } from '../../components/data.jsx';
import { PropertyFilter } from '../finance/panels.jsx';
import { useListState } from '../../lib/list-search.js';
import { DocumentCheckCreateSheet } from './create-sheet.jsx';
import { fiscalYearOptions, lastDoneStep } from './utils.js';

/** Belegprüfung › Liste (F-801): je Liegenschaft und Abrechnungsjahr, Filter in der URL. */
export function DocumentChecksListPage() {
  const api = useApi();
  const { data: me } = useMe();
  const navigate = useNavigate();
  const search = useSearch({ strict: false });
  const list = useListState(search);
  const [createOpen, setCreateOpen] = useState(false);
  const params = { page: list.apiParams.page, per_page: list.apiParams.per_page, filter: { property_id: search.property, fiscal_year: search.year } };
  const { data, error, isPending, isFetching, refetch } = useQuery(adminQueries.documentChecks(api, params));
  const columns = useMemo(() => checkColumns(), []);

  return (
    <>
      <PageHeader
        title="Belegprüfung"
        description="Prüfungen je Liegenschaft und Abrechnungsjahr: Ordner, Kassenprüfer, Bestätigungen und Versand an die Eigentümer."
        actions={
          hasPermission(me, 'document-checks.manage') && (
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus aria-hidden="true" /> Belegprüfung anlegen
            </Button>
          )
        }
      />
      <FilterBar>
        <PropertyFilter value={search.property} onChange={(v) => list.setFilter('property', v)} />
        <FilterSelect label="Abrechnungsjahr" value={search.year} onChange={(v) => list.setFilter('year', v)} options={fiscalYearOptions()} />
      </FilterBar>
      {error ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : (
        <DataTable
          label="Belegprüfungen"
          columns={columns}
          data={data?.data}
          rowCount={data?.meta?.total}
          isLoading={isPending}
          isFetching={isFetching}
          getRowId={(row) => String(row.id)}
          pagination={list.pagination}
          onPaginationChange={list.onPaginationChange}
          emptyTitle="Keine Belegprüfungen"
          emptyDescription="Für die gewählten Filter wurde noch keine Belegprüfung angelegt."
        />
      )}
      <DocumentCheckCreateSheet
        open={createOpen}
        onOpenChange={setCreateOpen}
        defaultPropertyId={search.property}
        onCreated={(check) => navigate({ to: '/belegpruefung/$checkId', params: { checkId: String(check.id) } })}
      />
    </>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- Spalten werden im Liegenschafts-Tab wiederverwendet
export function checkColumns() {
  return [
    {
      id: 'fiscal_year',
      header: 'Abrechnungsjahr',
      enableSorting: false,
      cell: ({ row }) => (
        <Link to="/belegpruefung/$checkId" params={{ checkId: String(row.original.id) }} className="numeric font-medium text-primary hover:underline">
          Belegprüfung {row.original.fiscal_year}
        </Link>
      ),
    },
    { id: 'property', header: 'Liegenschaft', enableSorting: false, cell: ({ row }) => <PropertyLink property={row.original.property} /> },
    {
      id: 'due_on',
      header: 'Abschluss bis',
      enableSorting: false,
      cell: ({ row }) => <span className="numeric">{formatDate(row.original.due_on)}</span>,
    },
    {
      id: 'status',
      header: 'Stand',
      enableSorting: false,
      cell: ({ row }) => {
        const step = lastDoneStep(row.original);
        return <Badge variant={row.original.is_completed ? 'success' : step.id === 'created' ? 'neutral' : 'info'}>{step.label}</Badge>;
      },
    },
  ];
}
